import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import {
  PaginationType,
  ResetApiState,
  ResetPaginationState,
  ResponseWrapper,
} from "../../redux.type";
import { queryStringGenerator } from "../../redux.util";
import { Sample, SampleStateTypes } from "./Sample.util";

const name = "sample";
const extraActions = createExtraActions();

const SampleInitialState: SampleStateTypes = {
  createSample: ResetApiState(null),
  resetCreateSample: ResetApiState(null),
  editSample: ResetApiState(null),
  resetEditSample: ResetApiState(null),
  deleteSample: ResetApiState(null),
  resetDeleteSample: ResetApiState(null),
  samples: ResetApiState([]),
  pagedSamples: ResetApiState(ResetPaginationState()),
  sample: ResetApiState(null),
  resetSample: ResetApiState(null),
  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const createSample = createAsyncThunk<ResponseWrapper<Sample>, any>(
    `/create-${name}`,
    async (data: any, thunkApi) =>
      axios
        .post(`/${name}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCreateSample = createAction(`/reset-create-${name}`);

  const editSample = createAsyncThunk<ResponseWrapper<Sample>, any>(
    `/edit-${name}`,
    async ({ data, id }: any, thunkApi) =>
      axios
        .put(`/${name}/${id}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetEditSample = createAction(`/reset-edit-${name}`);

  const deleteSample = createAsyncThunk<number, any>(
    `/delete-${name}`,
    async (id: any, thunkApi) =>
      axios
        .delete(`${name}/${id}`)
        .then((response) => {
          if (response.data.success) {
            return id;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error?.response?.data);
        })
  );

  const resetDeleteSample = createAction(`/reset-delete-${name}`);

  const getSamples = createAsyncThunk<Sample[], any>(
    `/get-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getPagedSamples = createAsyncThunk<PaginationType<Sample[]>, any>(
    `/get-paged-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}/paged${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getSample = createAsyncThunk<Sample, any>(
    `/get-${name}`,
    async (id: any, thunkApi) =>
      axios
        .get(`${name}/${id}`)
        .then((response) => response.data)
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetSample = createAction(`/reset-get-${name}`);

  const resetStates = createAction(`/reset-states`);

  return {
    createSample,
    resetCreateSample,
    editSample,
    resetEditSample,
    deleteSample,
    resetDeleteSample,
    getSamples,
    getPagedSamples,
    getSample,
    resetSample,
    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<SampleStateTypes>
) {
  return {
    ...createSampleReducer(),
    ...resetCreateSample(),
    ...editSampleReducer(),
    ...resetEditSample(),
    ...deleteSampleReducer(),
    ...resetDeleteSample(),
    ...samplesReducer(),
    ...pagedSamplesReducer(),
    ...sampleReducer(),
    ...resetSample(),
    ...resetStatesReducer(),
  };

  function createSampleReducer() {
    return {
      ...builder.addCase(extraActions.createSample.pending, (state, action) => {
        state.createSample = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.createSample.fulfilled,
        (state, action) => {
          state.createSample = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createSample.rejected,
        (state, action) => {
          state.createSample = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetCreateSample() {
    return {
      ...builder.addCase(extraActions.resetCreateSample, (state, action) => {
        state.resetCreateSample = ResetApiState(true);
        state.createSample = ResetApiState(null);
      }),
    };
  }

  function editSampleReducer() {
    return {
      ...builder.addCase(extraActions.editSample.pending, (state, action) => {
        state.editSample = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.editSample.fulfilled, (state, action) => {
        state.editSample = {
          loading: false,
          payload: action.payload.message,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.editSample.rejected, (state, action) => {
        state.editSample = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetEditSample() {
    return {
      ...builder.addCase(extraActions.resetEditSample, (state, action) => {
        state.resetEditSample = ResetApiState(true);
        state.editSample = ResetApiState(null);
      }),
    };
  }

  function deleteSampleReducer() {
    return {
      ...builder.addCase(extraActions.deleteSample.pending, (state, action) => {
        state.deleteSample = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.deleteSample.fulfilled,
        (state, action) => {
          state.deleteSample = {
            loading: false,
            payload: true,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteSample.rejected,
        (state, action) => {
          state.deleteSample = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDeleteSample() {
    return {
      ...builder.addCase(extraActions.resetDeleteSample, (state, action) => {
        state.resetDeleteSample = ResetApiState(true);
        state.deleteSample = ResetApiState(null);
      }),
    };
  }

  function samplesReducer() {
    return {
      ...builder.addCase(extraActions.getSamples.pending, (state, action) => {
        state.samples = {
          loading: true,
          payload: [],
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getSamples.fulfilled, (state, action) => {
        state.samples = {
          loading: false,
          payload: action.payload,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getSamples.rejected, (state, action) => {
        state.samples = {
          loading: false,
          payload: [],
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function pagedSamplesReducer() {
    return {
      ...builder.addCase(
        extraActions.getPagedSamples.pending,
        (state, action) => {
          state.pagedSamples = {
            loading: true,
            payload: ResetPaginationState(),
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedSamples.fulfilled,
        (state, action) => {
          state.pagedSamples = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedSamples.rejected,
        (state, action) => {
          state.pagedSamples = {
            loading: false,
            payload: ResetPaginationState(),
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function sampleReducer() {
    return {
      ...builder.addCase(extraActions.getSample.pending, (state, action) => {
        state.sample = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getSample.fulfilled, (state, action) => {
        state.sample = {
          loading: false,
          payload: action.payload,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getSample.rejected, (state, action) => {
        state.sample = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetSample() {
    return {
      ...builder.addCase(extraActions.resetSample, (state, action) => {
        state.resetSample = ResetApiState(true);
        state.sample = ResetApiState(null);
      }),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.createSample = ResetApiState(null);
        state.resetCreateSample = ResetApiState(null);
        state.editSample = ResetApiState(null);
        state.resetEditSample = ResetApiState(null);
        state.deleteSample = ResetApiState(null);
        state.resetDeleteSample = ResetApiState(null);
        state.samples = ResetApiState([]);
        state.sample = ResetApiState(null);
        state.resetSample = ResetApiState(null);
        state.resetSample = ResetApiState(null);
      }),
    };
  }
}

const SampleSlice = createSlice({
  name,
  initialState: SampleInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const SampleActions = { ...SampleSlice.actions, ...extraActions };
export const SampleReducer = SampleSlice.reducer;
